import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react'

import { createPortal } from 'react-dom'

import {
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
  MouseSensor,
  //  closestCenter,
  rectIntersection,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type DropAnimation,
  type UniqueIdentifier,
} from '@dnd-kit/core'
import styled from '@emotion/styled'
import uniqid from 'uniqid'

import {
  ACTION_BUTTON_TYPE_DELETE,
  ButtonAction,
} from '../ButtonAction/ButtonAction.js'
import { GRID_VARIANT_COLLAPSED } from '../Grid/Grid.constants.js'
import { Grid, GridCell } from '../Grid/Grid.js'
import { ModalConfirm } from '../ModalConfirm/ModalConfirm.js'

import {
  StyledStageItem,
  StyledStageItemContent,
  StyledStageItemControls,
  StyledStageItemExtraInfo,
  StyledWidgetListItem,
} from './FormBuilder.styles.ts'
import { FormBuilderEditPanel } from './FormBuilderEditPanel.js'
import { FormBuilderStage } from './FormBuilderStage.tsx'
import { FormBuilderTabPanel, FormBuilderTabs } from './FormBuilderTabs.js'
import { FormBuilderToolbox } from './FormBuilderToolbox.tsx'

const Container = styled.div`
  position: relative;
`

interface DeleteAction {
  type: 'delete'
  payload: {
    id: UniqueIdentifier
  }
}

interface ReorderAction {
  type: 'reorder'
  payload: {
    from: number
    to: number
  }
}

interface AddAction {
  type: 'add'
  payload: {
    id: UniqueIdentifier
    component: string
    position: number
  }
}

interface EditAction {
  type: 'edit'
  payload: {
    id: UniqueIdentifier
    data: any
  }
}

interface FormBuilderProps {
  className?: string
  'data-mu-test'?: string
  onChange: (a: DeleteAction | ReorderAction | AddAction | EditAction) => void
  items: any[]
  widgets: any[]
  EmptyStateComponent: React.ComponentType
  modalDeleteLabels: {
    title: string
    message: ReactNode
    confirm: string
    cancel: string
  }
  tabsLabels: {
    toolbox: string
    setup: string
  }
  toolboxLabels: {
    title: string
    description: string
  }
  globalConfig: any
  globalDefaults: any
}

const DefaultEmptyState: FC = () => null

const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
}

const getData = (prop: any): any => {
  return prop?.data?.current ?? {}
}

const FormBuilderDefaultProps: Pick<
  FormBuilderProps,
  'modalDeleteLabels' | 'tabsLabels' | 'toolboxLabels' | 'EmptyStateComponent'
> = {
  EmptyStateComponent: DefaultEmptyState,
  modalDeleteLabels: {
    title: 'Delete element',
    message: 'Are you sure you want to remove this form element?',
    confirm: 'OK',
    cancel: 'Cancel',
  },
  tabsLabels: {
    toolbox: 'Elements',
    setup: 'Setup',
  },
  toolboxLabels: {
    title: 'Form elements',
    description: 'Drag these blocks to the left to build your form',
  },
}

interface ModalDeleteState {
  id?: UniqueIdentifier
  show: boolean
}

export const FormBuilder: FC<FormBuilderProps> = ({
  className,
  'data-mu-test': dataAttributeTest,
  onChange,
  items,
  widgets,
  globalConfig,
  globalDefaults,
  EmptyStateComponent,
  ...props
}) => {
  const modalDeleteLabels =
    props.modalDeleteLabels ?? FormBuilderDefaultProps.modalDeleteLabels

  const tabsLabels = props.tabsLabels ?? FormBuilderDefaultProps.tabsLabels

  const toolboxLabels =
    props.toolboxLabels ?? FormBuilderDefaultProps.toolboxLabels

  const [dragFromToolbox, setDragFromToolbox] = useState<boolean>(false)
  const [dropIndex, setDropIndex] = useState<number | undefined>(undefined)

  const [modalDelete, setModalDelete] = useState<ModalDeleteState>({
    show: false,
    id: undefined,
  })
  const [editingItem, setEditingItem] = useState<UniqueIdentifier | undefined>(
    undefined,
  )
  const [tab, setTab] = useState<'toolbox' | 'setup'>('toolbox')
  const [dragging, setDragging] = useState<boolean>(false)
  const [height, setHeight] = useState<number>(0)
  const [dropAnimation, setDropAnimation] = useState<DropAnimation | null>(null)

  //  toolbox component being dragged, if any
  const [toolboxComponent, setToolboxComponent] = useState<string | undefined>(
    undefined,
  )

  //
  const [activeId, setActiveId] = useState<UniqueIdentifier | undefined>(
    undefined,
  )

  const tabOptions = useMemo(() => {
    return [
      { value: 'toolbox', label: tabsLabels.toolbox },
      {
        value: 'setup',
        label: tabsLabels.setup,
        disabled: editingItem !== undefined,
      },
    ]
  }, [editingItem, tabsLabels])

  //

  const handleConfirmDeleteItem = useCallback((): void => {
    if (modalDelete.id !== undefined) {
      setEditingItem(undefined)
      setTab('toolbox')
      //
      const deleteId = modalDelete.id
      // onDelete(deleteId)
      onChange({
        type: 'delete',
        payload: {
          id: deleteId,
        },
      })

      setModalDelete({ show: false, id: undefined })
    }
  }, [modalDelete, onChange])

  const handleEditItem = useCallback((id: UniqueIdentifier) => {
    setEditingItem(id)

    setTab('setup')
  }, [])

  const handleDeleteItem = useCallback((draggableId: UniqueIdentifier) => {
    setModalDelete({ show: true, id: draggableId })
  }, [])

  const handleDeleteCurrentItem = useCallback(() => {
    if (editingItem !== undefined) {
      handleDeleteItem(editingItem)
    }
  }, [editingItem, handleDeleteItem])

  const handleCancelDeleteItem = useCallback(() => {
    setModalDelete({ show: false, id: undefined })
  }, [])

  const handleChangeTab = useCallback((v: 'toolbox' | 'setup') => {
    setTab(v)
    setEditingItem(undefined)
  }, [])

  const handleChangeWidget = useCallback(
    (data: any) => {
      if (editingItem !== undefined) {
        onChange({
          type: 'edit',
          payload: {
            id: editingItem,
            data,
          },
        })
      }
    },
    [editingItem, onChange],
  )

  //  resize
  const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
    const h = entries[0]?.contentRect?.height

    if (h !== undefined) {
      setHeight(h)
    }
  }, [])

  const observer = useRef(new ResizeObserver(handleResize))

  useEffect(() => {
    const el = document.querySelector('#stage-form-builder')
    if (el !== null) {
      observer.current.observe(el)
    }

    const currentCopy = observer.current

    return () => {
      if (currentCopy !== null) {
        currentCopy.disconnect()
      }
    }
  }, [])

  //  /resize

  const memoItems = useMemo(
    () => items.map((o) => ({ ...o, id: o.draggableId })),
    [items],
  )

  const handleDragStart = (event: DragStartEvent): void => {
    const { active } = event

    const activeData = getData(active)

    setActiveId(active.id)
    setDragging(true)

    if (activeData.fromToolbox === true) {
      setToolboxComponent(active.id as string)
    }

    setDropAnimation(
      activeData.fromToolbox === true ? null : dropAnimationConfig,
    )
  }

  const handleDragOver = (event: DragOverEvent): void => {
    const { active, over } = event

    const activeData = getData(active)
    const overData = getData(over)

    if (over === null) {
      //  fuori da drop zone
      setDropIndex(undefined)
    } else if (
      //  sopra elemento esistente
      activeData.type === 'toolbox' &&
      overData.type === 'stage-item'
    ) {
      const idx = memoItems.findIndex((o) => o.id === over.id)
      setDropIndex(idx)
    } else if (activeData.type === 'toolbox' && overData.type === 'stage') {
      //  limite drop area es: fondo della lista
      setDropIndex(-1)
    }

    setDragFromToolbox(activeData.fromToolbox === true)
  }

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event

    const activeData = getData(active)

    if (activeData.type === 'toolbox' && over !== null) {
      const destIndex = memoItems.findIndex((o) => o.id === over.id)
      const position = destIndex > -1 ? destIndex : memoItems.length
      const newId = uniqid(`${active.id}|`)

      onChange({
        type: 'add',
        payload: {
          id: newId,
          component: active.id as string,
          position,
        },
      })

      setEditingItem(newId)
      setTab('setup')
    } else {
      if (over !== null && active.id !== over.id) {
        const oldIndex = memoItems.findIndex((o) => o.id === active.id)
        const newIndex = memoItems.findIndex((o) => o.id === over.id)

        onChange({
          type: 'reorder',
          payload: {
            from: oldIndex,
            to: newIndex,
          },
        })

        //
      }
    }

    setDragging(false)
    setToolboxComponent(undefined)
    setActiveId(undefined)

    setDragFromToolbox(false)
    setDropIndex(undefined)

    setTimeout(() => {
      setDropAnimation(null)
    }, 500)
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      onActivation: ({ event }: { event: PointerEvent }) => {
        if (event.target !== null) {
          const t = event.target as HTMLElement

          const closestDeleteItem: HTMLElement | null = t.closest(
            "[data-mu-test~='action-delete']",
          )

          if (closestDeleteItem !== null) {
            closestDeleteItem.click()
          }

          const closestStageItem: HTMLElement | null = t.closest(
            "[data-mu-test~='stage-item']",
          )

          if (closestStageItem !== null) {
            closestStageItem.click()
          }
        }
      },
      activationConstraint: {
        distance: 5,
        //  delay: 100,
        //  tolerance: 0,
      },
    }),
  )

  const currentWidget = useMemo(() => {
    if (toolboxComponent !== undefined) {
      return widgets.find((o) => o.component === toolboxComponent)
    }

    return undefined
  }, [widgets, toolboxComponent])

  const currentDraggingItem = useMemo(() => {
    return memoItems.find((o) => o.id === activeId)
  }, [memoItems, activeId])

  const currentItem = useMemo(
    () => memoItems.find((o) => o.id === editingItem),
    [editingItem, memoItems],
  )

  return (
    <Container className={className} data-mu-test={dataAttributeTest}>
      <ModalConfirm
        variant='delete'
        show={modalDelete.show}
        title={modalDeleteLabels.title}
        message={modalDeleteLabels.message}
        onClickConfirm={handleConfirmDeleteItem}
        onClickCancel={handleCancelDeleteItem}
        stringConfirm={modalDeleteLabels.confirm}
        stringCancel={modalDeleteLabels.cancel}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        modifiers={
          [
            /* restrictToVerticalAxis, restrictToFirstScrollableAncestor */
          ]
        }
      >
        <Grid cols={12} variant={GRID_VARIANT_COLLAPSED}>
          <GridCell span={9}>
            <FormBuilderStage
              items={memoItems}
              onEditItem={handleEditItem}
              onDeleteItem={handleDeleteItem}
              selectedId={editingItem}
              EmptyStateComponent={EmptyStateComponent}
              globalConfig={globalConfig}
              dragFromToolbox={dragFromToolbox}
              dropIndex={dropIndex}
            />
          </GridCell>
          <GridCell span={3}>
            <FormBuilderTabs
              options={tabOptions}
              value={tab}
              onChange={handleChangeTab}
              dragging={dragging}
            >
              <FormBuilderTabPanel data-tab='toolbox'>
                <FormBuilderToolbox
                  height={height}
                  title={toolboxLabels.title}
                  description={toolboxLabels.description}
                  options={widgets}
                  globalDefaults={globalDefaults}
                />
              </FormBuilderTabPanel>

              <FormBuilderTabPanel data-tab='setup'>
                {currentItem !== undefined && (
                  <FormBuilderEditPanel
                    title={currentItem.title}
                    onClickDelete={handleDeleteCurrentItem}
                  >
                    {currentItem.renderEditComponent({
                      ...currentItem.config,
                      ...globalConfig,
                      onChange: handleChangeWidget,
                    })}
                  </FormBuilderEditPanel>
                )}
              </FormBuilderTabPanel>
            </FormBuilderTabs>
          </GridCell>
        </Grid>
        {createPortal(
          <DragOverlay
            adjustScale={false}
            dropAnimation={dropAnimation}
            zIndex={1100}
          >
            {currentWidget !== undefined && (
              <StyledWidgetListItem isDragging>
                {currentWidget.renderStageComponent(
                  currentWidget.defaultConfig(globalDefaults),
                )}
              </StyledWidgetListItem>
            )}

            {activeId !== undefined && currentDraggingItem !== undefined && (
              <StyledStageItem selected={false} invalid={false} isDragging>
                <StyledStageItemControls className='controls' selected>
                  <ButtonAction type={ACTION_BUTTON_TYPE_DELETE} />
                </StyledStageItemControls>
                <StyledStageItemContent>
                  {currentDraggingItem.renderStageComponent(
                    currentDraggingItem.config,
                  )}
                </StyledStageItemContent>
                <StyledStageItemExtraInfo>
                  {currentDraggingItem.renderStageExtraInfoComponent?.({
                    ...currentDraggingItem.config,
                    ...globalConfig,
                  })}
                </StyledStageItemExtraInfo>
              </StyledStageItem>
            )}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </Container>
  )
}

export const FB_ACTION_ADD_ELEMENT = 'add'
export const FB_ACTION_REORDER = 'reorder'
export const FB_ACTION_EDIT_ELEMENT = 'edit'
export const FB_ACTION_DELETE_ELEMENT = 'delete'
